import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {
    ClearPolicy,
    SchedulePolicy,
    useTimeout,
} from "@khanacademy/wonder-blocks-timing";

import type {SpeechToTextResult} from "./chat-input/use-speech-to-text";

interface SpeechRecognitionEvent extends Event {
    resultIndex: number;
    results: SpeechRecognitionResultList;
}

interface SpeechRecognition {
    /**
     * When set `true`, the speech recognition system should continue listening
     * and processing speech even after the user has paused or stopped speaking.
     */
    continuous: boolean;
    /**
     * Determines whether iterim results should be returned (true) or not
     * (false).
     */
    interimResults: boolean;
    /**
     * Specifics the language of the speech to be recognized. The language is
     * represented in a BCP 47 language tag (e.g. en-US).
     */
    lang: string;
    /**
     * Method to start the speech recognition service.
     */
    start: () => void;
    /**
     * Method to stop the speech recognition service from listening to incoming
     * audio and attempts to return a SpeechRecognitionResult captured.
     */
    stop: () => void;
    /**
     * Triggered when a speech recognition error occurs.
     */
    onerror: (event: SpeechRecognitionEvent) => void;
    /**
     * Triggered when the speech recognition service returns a result
     */
    onresult: (event: SpeechRecognitionEvent) => void;
    /**
     * Triggered when the speech recognition service has begun listening to
     * incoming audio. It’s triggered when the start() method of a
     * SpeechRecognition object is called, indicating that the service is ready
     * to start transcribing speech.
     */
    onstart: () => void;
    /**
     * Triggered when the speech recognition service has ended/disconnected.
     */
    onend: () => void;
    /**
     * Triggered when the speech recognized by the recognition service has
     * stopped being detected.
     */
    onspeechend: () => void;
    /**
     * Triggered when the user has started speaking. In other words, it’s
     * triggered when sound that is recognized as speech is first detected.
     */
    onspeechstart: () => void;
}

enum VoiceCommand {
    StopListening = "stop listening",
}

const PAUSE_THRESHOLD_MS = 7000;

/**
 * A hook that provides speech-to-text functionality using native browser
 * transcription. Audio is transcribed using the SpeechRecorder interface,
 * provided by the Web Speech API. This only supported on Chrome.
 */
export const useSpeechRecognition = (): SpeechToTextResult => {
    const [isOn, setIsOn] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);

    const [transcript, setTranscript] = useState("");
    const [interimTranscript, setInterimTranscript] = useState("");
    const speechRecognition = useRef<SpeechRecognition | null>(null);

    // When we detect a long pause in speech, we will automatically turn off
    // the speech recognition service.
    const pauseTimeout = useTimeout(() => setIsOn(false), PAUSE_THRESHOLD_MS, {
        clearPolicy: ClearPolicy.Cancel,
        schedulePolicy: SchedulePolicy.OnDemand,
    });

    useEffect(() => {
        if (isOn) {
            speechRecognition.current?.start();
        } else {
            speechRecognition.current?.stop();
        }
    }, [isOn]);

    const toggleSpeechRecognition = useCallback(() => setIsOn((on) => !on), []);

    const isSupported = useMemo(isSpeechRecognitionSupported, []);
    const isTestRunner = process && process?.env.NODE_ENV === "test";
    // Return an object with noop handlers for unsupported or test environment.
    if (!isSupported || isTestRunner) {
        return {
            isSupportedBrowser: false,
            lastTranscription: "",
            interimTranscription: "",
            on: false,
            transcribing: false,
            toggleSpeechRecognition: () => {},
        };
    }

    if (!speechRecognition.current) {
        const voiceCommands: Map<VoiceCommand, () => void> = new Map([
            [VoiceCommand.StopListening, () => setIsOn(false)],
        ]);

        speechRecognition.current = initSpeechRecognition({
            onStart: () => {
                setIsOn(true);
            },
            onEnd: () => {
                pauseTimeout.clear();
                // Speech recognition can timeout so we want to ensure the
                // state is reflective of when the timeout happens.
                setIsTranscribing(false);
                setTranscript("");
                setInterimTranscript("");
                setIsOn(false);
            },
            onSpeechStart: () => setIsTranscribing(true),
            onSpeechEnd: () => setIsTranscribing(false),
            onError: () => setIsOn(false),
            onResult: (event: SpeechRecognitionEvent) => {
                // We renew the timeout when we receive a brand new result
                pauseTimeout.clear();
                setIsTranscribing(true);

                const index = event.resultIndex;
                const transcript =
                    event.results[index][0].transcript.toLowerCase();
                const isFinal = event.results[index].isFinal;

                const handler = voiceCommands.get(
                    transcript.trim() as VoiceCommand,
                );

                if (handler) {
                    return handler();
                }

                if (isFinal) {
                    pauseTimeout.set();
                    setTranscript(transcript);
                    setIsTranscribing(false);
                } else if (event.results[index][0].confidence > 0.8) {
                    setInterimTranscript(transcript);
                }
            },
        });
    }

    return {
        on: isOn,
        transcribing: isTranscribing,
        lastTranscription: transcript,
        interimTranscription: interimTranscript,
        toggleSpeechRecognition,
        isSupportedBrowser: true,
    };
};

// HACK(mattmorgan) We can't use feature detection to decide if
// our browser supports speech recognition, because many browsers have
// SpeechRecognition interfaces, but the feature doesn't actually work yet.
// So, we use other methods.
const isSpeechRecognitionSupported = (): boolean => {
    // SpeechRecognition is currently only supported in Chrome atm.
    if (!/(Chrome)/.test(navigator.userAgent)) {
        return false;
    }

    // Brave is Chromium, but doesn't support SpeechRecognition.
    // @ts-expect-error - TS2339 - Property 'brave' does not exist on type 'Navigator'.
    if (navigator.brave) {
        return false;
    }
    // Edge lists "Chrome" in their user agent, but their SpeechRecognition
    // implementation is flaky. We need to explicitly disallow this browser for
    // now.
    if (/(Edg)/.test(navigator.userAgent)) {
        return false;
    }

    return true;
};

const initSpeechRecognition = ({
    onStart,
    onEnd,
    onSpeechStart,
    onSpeechEnd,
    onError,
    onResult,
}: {
    onStart: () => void;
    onEnd: () => void;
    onSpeechStart: () => void;
    onSpeechEnd: () => void;
    onError: (event: SpeechRecognitionEvent) => void;
    onResult: (event: SpeechRecognitionEvent) => void;
}): SpeechRecognition | null => {
    const SpeechRecognition =
        // @ts-expect-error - TS2339 - Property 'webkitSpeechRecognition' and
        // 'SpeechRecognition' does not exist on type 'Window & typeof globalThis'.
        window.webkitSpeechRecognition || window.SpeechRecognition;

    if (!isSpeechRecognitionSupported() || !SpeechRecognition) {
        return null;
    }

    const recognition: SpeechRecognition = new SpeechRecognition();
    // TODO(GL-302): Handle locale/language based on kaLocale.
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.onspeechstart = onSpeechStart;
    recognition.onspeechend = onSpeechEnd;
    recognition.onstart = onStart;
    recognition.onend = onEnd;
    recognition.onerror = onError;
    recognition.onresult = onResult;

    return recognition;
};
